import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { apiClient } from '../api';
import {
  isPushSupported,
  getPushPermission,
  subscribeToPush,
  unsubscribeFromPush,
  ensureServiceWorker,
} from '../lib/push';

const PushToggle = () => {
  const [supported, setSupported] = useState(false);
  const [perm, setPerm] = useState('default');
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prefs, setPrefs] = useState({
    push_enabled: true, push_messages: true, push_views: true, digest_emails: true, muted_users: [],
  });

  useEffect(() => {
    const s = isPushSupported();
    setSupported(s);
    apiClient.get('/notifications/prefs')
      .then((r) => setPrefs(r.data))
      .catch((err) => console.warn('Failed to load notification prefs:', err?.message || err));
    if (!s) return;
    setPerm(getPushPermission());
    (async () => {
      try {
        const reg = await ensureServiceWorker();
        const sub = await reg.pushManager.getSubscription();
        setEnabled(!!sub);
      } catch (err) {
        console.warn('Failed to read push subscription state:', err?.message || err);
      }
    })();
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const r = await subscribeToPush();
      if (r.ok) {
        setEnabled(true);
        setPerm('granted');
        toast.success('Notifications enabled', {
          description: 'You\'ll get a heads-up when someone messages or views you.',
        });
      } else if (r.reason === 'denied') {
        setPerm('denied');
        toast.error('Permission blocked', {
          description: 'Enable notifications in your browser settings to receive alerts.',
        });
      } else {
        toast.error('Could not enable', { description: `Reason: ${r.reason}` });
      }
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setEnabled(false);
      toast('Notifications disabled');
    } finally { setBusy(false); }
  };

  const updatePref = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try {
      const r = await apiClient.put('/notifications/prefs', next);
      setPrefs(r.data);
    } catch (e) {
      toast.error('Could not save preference');
    }
  };

  return (
    <div className="bg-fnl-purple/40 rounded-xl p-4 border border-purple-900/40 space-y-4" data-testid="push-toggle">
      <div className="flex items-start gap-3">
        {enabled ? <BellRing className="w-5 h-5 text-fnl-pink mt-0.5 shrink-0" /> : <Bell className="w-5 h-5 text-purple-300 mt-0.5 shrink-0" />}
        <div className="flex-1">
          <div className="text-white font-semibold text-sm">Push notifications</div>
          <div className="text-purple-300 text-xs">
            Get notified about new messages and profile views — even when Flirty is closed.
          </div>
        </div>
        <PushSubscribeControl
          supported={supported}
          enabled={enabled}
          busy={busy}
          perm={perm}
          onEnable={enable}
          onDisable={disable}
        />
      </div>

      <div className="border-t border-purple-900/40 pt-3 space-y-3" data-testid="push-preferences">
        <PrefRow
          title="All notifications"
          desc="Master switch — turn off to silence everything."
          checked={prefs.push_enabled}
          onChange={(v) => updatePref({ push_enabled: v })}
          testid="pref-push-enabled"
        />
        <PrefRow
          title="New messages"
          desc="Be alerted when someone sends you a message."
          checked={prefs.push_messages}
          disabled={!prefs.push_enabled}
          onChange={(v) => updatePref({ push_messages: v })}
          testid="pref-push-messages"
        />
        <PrefRow
          title="Profile views"
          desc="Be alerted when someone checks out your profile."
          checked={prefs.push_views}
          disabled={!prefs.push_enabled}
          onChange={(v) => updatePref({ push_views: v })}
          testid="pref-push-views"
        />
        <PrefRow
          title="Weekly digest email"
          desc="Sunday-evening email recapping who liked you that week."
          checked={prefs.digest_emails}
          onChange={(v) => updatePref({ digest_emails: v })}
          testid="pref-digest-emails"
        />
        {prefs.muted_users && prefs.muted_users.length > 0 ? (
          <div className="text-xs text-purple-300 pt-1 flex items-center justify-between" data-testid="pref-muted-count">
            <span>
              🔇 {prefs.muted_users.length} user{prefs.muted_users.length === 1 ? '' : 's'} muted
            </span>
            <Link to="/muted" className="text-fnl-pink hover:text-fnl-pink/80 font-semibold" data-testid="pref-muted-manage">
              Manage →
            </Link>
          </div>
        ) : (
          <div className="text-xs text-purple-400 pt-1">
            <Link to="/muted" className="hover:text-fnl-pink underline">
              Manage muted users
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

const PushSubscribeControl = ({ supported, enabled, busy, perm, onEnable, onDisable }) => {
  if (!supported) {
    return (
      <span className="inline-flex items-center gap-1 text-purple-400 text-xs">
        <BellOff className="w-4 h-4" /> Not supported
      </span>
    );
  }
  if (enabled) {
    return (
      <Button
        onClick={onDisable}
        disabled={busy}
        variant="outline"
        className="bg-fnl-purple-light/40 border-purple-700 text-white hover:bg-fnl-purple-light hover:text-white"
        data-testid="push-toggle-disable"
      >
        Disable
      </Button>
    );
  }
  return (
    <Button
      onClick={onEnable}
      disabled={busy || perm === 'denied'}
      className="bg-fnl-pink hover:bg-fnl-pink text-white"
      data-testid="push-toggle-enable"
    >
      {perm === 'denied' ? 'Blocked' : 'Enable'}
    </Button>
  );
};

const PrefRow = ({ title, desc, checked, onChange, disabled, testid }) => (
  <div className={`flex items-center justify-between gap-3 ${disabled ? 'opacity-50' : ''}`}>
    <div className="flex-1">
      <div className="text-white text-sm font-medium">{title}</div>
      <div className="text-purple-300 text-xs">{desc}</div>
    </div>
    <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

export default PushToggle;
